import { useState, useEffect, useCallback } from 'react';

interface RecentVehicle {
  plate: string;
  brand?: string;
  model?: string;
  lastUsed: string;
}

interface RecentDriver {
  id: string;
  name: string;
  lastUsed: string;
}

interface RecentItems {
  vehicles: RecentVehicle[];
  drivers: RecentDriver[];
}

const STORAGE_KEY = 'recentItems';
const MAX_RECENT_ITEMS = 5;

export const useRecentItems = () => {
  const [recentItems, setRecentItems] = useState<RecentItems>({
    vehicles: [],
    drivers: [],
  });

  // Charger les éléments récents au montage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setRecentItems(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Erreur chargement éléments récents:', e);
    }
  }, []);

  // Sauvegarder un véhicule récent
  const addRecentVehicle = useCallback((vehicle: Omit<RecentVehicle, 'lastUsed'>) => {
    setRecentItems(prev => {
      // Supprimer les doublons
      const filtered = prev.vehicles.filter(v => v.plate !== vehicle.plate);
      
      // Ajouter en premier avec la date actuelle
      const updated: RecentItems = {
        ...prev,
        vehicles: [
          { ...vehicle, lastUsed: new Date().toISOString() },
          ...filtered,
        ].slice(0, MAX_RECENT_ITEMS),
      };
      
      // Sauvegarder dans localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Sauvegarder un chauffeur récent
  const addRecentDriver = useCallback((driver: Omit<RecentDriver, 'lastUsed'>) => {
    setRecentItems(prev => {
      // Supprimer les doublons
      const filtered = prev.drivers.filter(d => d.id !== driver.id);
      
      // Ajouter en premier avec la date actuelle
      const updated: RecentItems = {
        ...prev,
        drivers: [
          { ...driver, lastUsed: new Date().toISOString() },
          ...filtered,
        ].slice(0, MAX_RECENT_ITEMS),
      };
      
      // Sauvegarder dans localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Obtenir les suggestions pour un véhicule (auto-complétion)
  const getVehicleSuggestions = useCallback((query: string): RecentVehicle[] => {
    if (!query) return recentItems.vehicles.slice(0, 3);
    
    const lowerQuery = query.toLowerCase();
    return recentItems.vehicles.filter(v => 
      v.plate.toLowerCase().includes(lowerQuery) ||
      v.brand?.toLowerCase().includes(lowerQuery) ||
      v.model?.toLowerCase().includes(lowerQuery)
    );
  }, [recentItems.vehicles]);

  // Obtenir les suggestions pour un chauffeur (auto-complétion)
  const getDriverSuggestions = useCallback((query: string): RecentDriver[] => {
    if (!query) return recentItems.drivers.slice(0, 3);
    
    const lowerQuery = query.toLowerCase();
    return recentItems.drivers.filter(d => 
      d.name.toLowerCase().includes(lowerQuery)
    );
  }, [recentItems.drivers]);

  // Nettoyer tous les éléments récents
  const clearRecentItems = useCallback(() => {
    setRecentItems({ vehicles: [], drivers: [] });
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    recentItems,
    addRecentVehicle,
    addRecentDriver,
    getVehicleSuggestions,
    getDriverSuggestions,
    clearRecentItems,
  };
};

export default useRecentItems;







